/**
 * Algolia Dynamic Facets Integration
 *
 * This module integrates dynamic faceting into the existing algolia-collection.js
 * It extends the current faceting system to support:
 * - Collection and search-specific facet rules
 * - Dynamic facet ordering
 * - Conditional facet display based on available data
 * - Multi-phase facet rollout (Phase 1, 2, 3)
 *
 * INTEGRATION NOTES:
 * This file should be loaded BEFORE algolia-collection.js in your template
 * It extends the acceptedFacets array and modifies facet rendering logic
 */

(function () {
  'use strict';

  // Wait for AlgoliaFacetConfig to be available
  if (!window.AlgoliaFacetConfig) {
    console.warn('AlgoliaFacetConfig not loaded. Please load algolia-facet-config-manager.js first.');
    return;
  }

  // ==========================================
  // Extension: Dynamic Facets
  // ==========================================

  /**
   * Get dynamic facet list based on collection handle and search query
   * This function is called by algolia-collection.js during initialization
   */
  window.AlgoliaDynamicFacets = {
    /**
     * Get accepted facets for current collection/search
     * @param {String} collectionHandle - Shopify collection handle
     * @param {String} searchQuery - Search query text
     * @returns {Array} - Array of facet attribute names
     */
    getAcceptedFacets: function (collectionHandle, searchQuery) {
      const facetConfigs = window.AlgoliaFacetConfig.getFacetConfig(collectionHandle, searchQuery);
      return facetConfigs.map((config) => config.attribute);
    },

    /**
     * Get facet configuration (label, type, order) for rendering
     * @param {String} collectionHandle - Shopify collection handle
     * @param {String} searchQuery - Search query text
     * @returns {Object} - Map of facet attributes to their configs
     */
    getFacetConfigs: function (collectionHandle, searchQuery) {
      const facetConfigs = window.AlgoliaFacetConfig.getFacetConfig(collectionHandle, searchQuery);
      const configMap = {};
      facetConfigs.forEach((config) => {
        configMap[config.attribute] = config;
      });
      return configMap;
    },

    /**
     * Filter facets that should be displayed
     * @param {Object} allFacets - All facet data from Algolia response
     * @param {Object} facetConfigs - Facet configuration map
     * @returns {Object} - Filtered facets to display
     */
    filterDisplayableFacets: function (allFacets, facetConfigs) {
      const displayableFacets = {};
      Object.keys(allFacets).forEach((attr) => {
        const config = facetConfigs[attr];
        const facetData = allFacets[attr];

        if (window.AlgoliaFacetConfig.shouldDisplayFacet(facetData, config || {})) {
          displayableFacets[attr] = facetData;
        }
      });
      return displayableFacets;
    },

    /**
     * Order facets according to configuration
     * @param {Object} facets - Facet data object
     * @param {Object} facetConfigs - Facet configuration map
     * @returns {Array} - Ordered array of [attribute, data] pairs
     */
    orderFacets: function (facets, facetConfigs) {
      return Object.keys(facets)
        .map((attr) => ({
          attr,
          data: facets[attr],
          order: facetConfigs[attr]?.order || 999,
        }))
        .sort((a, b) => a.order - b.order)
        .map((item) => [item.attr, item.data]);
    },
  };

  // ==========================================
  // Helper: Update Accepted Facets
  // ==========================================

  /**
   * Initialize dynamic facets based on current page context
   * This should be called from algolia-collection.js init()
   */
  window.initializeDynamicFacets = function (collectionHandle) {
    // Get accepted facets for this collection
    const acceptedFacets = window.AlgoliaDynamicFacets.getAcceptedFacets(collectionHandle, '');

    // Store in global for algolia-collection.js to use
    window.__ALGOLIA_ACCEPTED_FACETS = acceptedFacets;

    return acceptedFacets;
  };

  // ==========================================
  // Extended Facet Labels
  // ==========================================

  /**
   * Extended facet label map for new Phase 1-3 facets
   * Merge with existing FACET_LABELS in algolia-collection.js
   */
  window.EXTENDED_FACET_LABELS = {
    // Phase 1
    'facets.material': 'MATERIAL',
    'facets.availability_type': 'AVAILABILITY',
    'facets.room': 'ROOM',
    'facets.style': 'STYLE',
    'facets.collection': 'COLLECTION',
    'facets.size': 'SIZE',

    // Phase 2
    'facets.shape': 'SHAPE',
    'facets.product_features': 'FEATURES',
    'facets.occasion': 'OCCASION',
    'facets.pattern': 'PATTERN',
    'facets.finish': 'FINISH',

    // Phase 3
    'facets.seating_capacity': 'SEATING CAPACITY',
    'facets.fabric_type': 'FABRIC TYPE',
    'facets.performance_fabric': 'PERFORMANCE FABRIC',
    'facets.subject_matter': 'SUBJECT MATTER',
    'facets.orientation': 'ORIENTATION',
    'facets.indoor_outdoor': 'INDOOR/OUTDOOR',
    'facets.set_size': 'SET SIZE',
    'facets.capacity': 'CAPACITY',
  };
})();
